/**
 * Site-wide AJAX cart drawer.
 *
 * Replaces the old add-to-cart → redirect-to-/cart behaviour. Opens on
 * add-to-cart and on the header cart icon, on every page.
 *
 * Architecture (ported from the NasoNeb v2 push cart, on the Airways DS
 * `air-cart-push --resize` shell — opening the cart shrinks the page and
 * reflows it beside the docked panel rather than covering it):
 *
 *   - Section Rendering API. /cart/add.js and /cart/change.js both carry
 *     `sections` + `sections_url`; the rendered HTML in the response replaces the
 *     panel. The panel is therefore always server-truth — no client-side money
 *     formatting, no DOM patching, no drift between markup and cart state.
 *   - Delegated listeners on `document`. The panel's innerHTML is swapped
 *     wholesale on every change, so nothing may hold a reference to a node
 *     inside it. Delegation survives re-render with zero re-wiring.
 *   - Progressive enhancement. The trigger is a real <a href="/cart">, the forms
 *     really POST to /cart/add, and remove is a real url_to_remove link. Every
 *     failure path falls back to navigation or a native form submit.
 *
 * NON-MODAL by design. The push cart leaves the page visible and interactive
 * beside the panel, so there is deliberately no scrim, no focus trap and no
 * scroll lock — `aria-modal="false"`. Trapping focus would contradict what the
 * component does. Escape-to-close, focus-in on open and focus-return on close
 * are all still here.
 *
 * The B2B catalog (sections/aero-shop-b2b.liquid) keeps its own /cart/update.js
 * flow and binds to .air-catalog__btn — this file never intercepts it. The two
 * stay in sync through a one-way notification each way, never a shared handler:
 *   - catalog -> drawer: the catalog calls window.AeroCartDrawer.refresh()
 *   - drawer -> catalog: this file dispatches `aero:cart:updated`
 * Each side only repaints its own DOM on receipt, so they cannot ping-pong.
 */
(function () {
  'use strict';

  var SELECTORS = {
    root: '[data-aero-cart-drawer]',
    panel: '#cart-drawer-panel',
    status: '[data-aero-cart-drawer-status]',
    open: '[data-air-cart-drawer-open]',
    close: '[data-air-cart-drawer-close]',
    addForm: '[data-aero-cart-add-form]',
    remove: '[data-aero-drawer-remove]',
    qty: '[data-aero-drawer-qty]',
    terms: '[data-aero-drawer-terms]',
    checkout: '[data-aero-drawer-checkout]',
    wallet: '[data-aero-drawer-dynamic-checkout]',
    bubble: '#cart-icon-bubble',
    fabCount: '[data-aero-cart-count]'
  };

  var reducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Update every cart count in the UI.
   *
   * There are TWO, with different zero-state conventions:
   *   - Header icon (#cart-icon-bubble .air-badge--count) — hides via the DS
   *     `:empty` rule, so zero must be '' and never '0'.
   *   - Mobile FAB ([data-aero-cart-count], .air-navbar-cart__count) — hides via
   *     the `hidden` attribute, so it keeps its number and toggles `hidden`.
   *
   * Both are SURGICAL — find-or-create the node and set textContent, never
   * replace a link's innerHTML. A destructive rewrite would strip the
   * cart-drawer open attributes the header link carries, and would delete the
   * badge node the B2B catalog page (aero-shop-b2b.liquid) looks up directly.
   */
  function updateCartBubble(count) {
    // Mobile FAB count — `hidden` attribute convention.
    document.querySelectorAll(SELECTORS.fabCount).forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });

    var bubble = document.querySelector(SELECTORS.bubble);
    if (!bubble) return;

    var badge = bubble.querySelector('.air-badge--count');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'air-badge air-badge--count';
      badge.setAttribute('aria-hidden', 'true');
      bubble.appendChild(badge);
    }
    badge.textContent = count > 0 ? count : '';

    // Screen-reader count. The two themes render this span with different
    // classes — TAH v2 uses `.air-sr-only`, production (Dawn-derived) uses
    // `.visually-hidden` — so match either and reuse whichever is present
    // rather than appending a second one on every update. New spans are created
    // with the class the surrounding theme actually ships.
    var srCount = bubble.querySelector('.air-sr-only, .visually-hidden');
    if (count > 0) {
      if (!srCount) {
        srCount = document.createElement('span');
        srCount.className = document.querySelector('.visually-hidden') ? 'visually-hidden' : 'air-sr-only';
        bubble.appendChild(srCount);
      }
      srCount.textContent = count + (count === 1 ? ' item in cart' : ' items in cart');
    } else if (srCount) {
      srCount.remove();
    }
  }

  /**
   * Tell other cart surfaces the cart changed.
   *
   * The B2B catalog (sections/aero-shop-b2b.liquid) renders its own rows,
   * quantities and order summary from the cart, and does not share a selector
   * with this drawer — so a removal or quantity change made here would leave
   * that page stale. It listens for this event and re-reads /cart.js.
   *
   * Fire-and-forget by design: nothing here depends on a listener existing, and
   * listeners must not call back into the drawer (that would ping-pong).
   */
  function notifyCartUpdated(cart) {
    document.dispatchEvent(
      new CustomEvent('aero:cart:updated', { detail: { cart: cart || null } })
    );
  }

  function initCartDrawer(root) {
    var panel = root.querySelector(SELECTORS.panel);
    var status = document.querySelector(SELECTORS.status);
    var addUrl = root.getAttribute('data-cart-add-url') || '/cart/add.js';
    var changeUrl = root.getAttribute('data-cart-change-url') || '/cart/change.js';
    var cartUrl = root.getAttribute('data-cart-url') || '/cart';
    var sectionId = root.getAttribute('data-cart-section') || 'aero-cart-drawer-section';
    var discountCode = (root.getAttribute('data-cart-discount') || '').trim();
    var lastTrigger = null;
    // One attach per page load — see attachDiscount().
    var attachAttempted = false;

    function isOpen() {
      return root.hasAttribute('data-air-open');
    }

    function announce(message) {
      if (status) status.textContent = message;
    }

    // No focus trap: the push cart is non-modal (aria-modal="false") and the
    // page stays interactive beside it. Trapping focus would contradict that.

    function open(trigger) {
      lastTrigger = trigger || null;
      // The mobile nav drawer is a top-layer popover: close it first, or the cart
      // (opened from the drawer's cart icon) would sit underneath it.
      var navDrawer = document.querySelector('.air-nav__main[popover]');
      if (navDrawer && navDrawer.matches(':popover-open')) navDrawer.hidePopover();
      root.setAttribute('data-air-open', '');
      document.querySelectorAll(SELECTORS.open).forEach(function (el) {
        el.setAttribute('aria-expanded', 'true');
      });

      // Move focus in after the slide-in so it is not visibly mid-transition.
      var closeBtn = panel && panel.querySelector('.air-cart-push__close');
      if (closeBtn) {
        window.setTimeout(
          function () {
            closeBtn.focus();
          },
          reducedMotion ? 0 : 300
        );
      }
    }

    function close() {
      if (!isOpen()) return;
      root.removeAttribute('data-air-open');
      document.querySelectorAll(SELECTORS.open).forEach(function (el) {
        el.setAttribute('aria-expanded', 'false');
      });
      // A trigger inside the now-closed nav drawer is hidden; return focus to the
      // drawer's menu button instead so focus is never lost (WCAG 2.4.3).
      var target = lastTrigger;
      if (target && target.closest && target.closest('.air-nav__main[popover]:not(:popover-open)')) {
        target = document.querySelector('[popovertarget="side-menu"]');
      }
      if (target && typeof target.focus === 'function') target.focus();
      lastTrigger = null;
    }

    /**
     * Swap in a freshly rendered panel.
     *
     * Unlike the implementation this was ported from, focus is NOT moved here.
     * Re-rendering happens after a quantity change or a removal too, and
     * yanking focus to the close button on every one of those is disorienting
     * for keyboard users. Focus is handled on open() only.
     */
    function renderPanel(html) {
      if (!panel || !html) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('.shopify-section') || doc.body;
      panel.innerHTML = fresh.innerHTML;
      syncCheckoutGate();
    }

    /**
     * Terms of Sale gate.
     *
     * The cart page's component (aero-component-terms-of-sale-agreement.js) is
     * hardcoded to `#cart` and binds once on DOMContentLoaded, so it can neither
     * see this panel nor survive the re-render. The drawer owns its own gate.
     *
     * The panel re-renders unchecked after every cart change, so this only ever
     * has to re-assert "disabled" — there is no stale enabled state to clear.
     */
    function syncCheckoutGate() {
      if (!panel) return;
      var terms = panel.querySelector(SELECTORS.terms);
      var agreed = Boolean(terms && terms.checked);

      // Deliberately NOT behind an early return on `checkout` — the wallet gate
      // below must still run if the checkout link is ever absent while wallet
      // buttons render, or the gate would silently no-op and leave them live.
      var checkout = panel.querySelector(SELECTORS.checkout);
      if (checkout) {
        checkout.setAttribute('aria-disabled', agreed ? 'false' : 'true');
        if (agreed) {
          checkout.removeAttribute('tabindex');
        } else {
          checkout.setAttribute('tabindex', '-1');
        }
      }

      // Accelerated (wallet) buttons hide their internals in a CLOSED shadow DOM,
      // so the buttons themselves cannot be disabled — gate the wrapper instead.
      // Class-based, unlike the cart page's component which sets inline styles and
      // therefore needs a MutationObserver to re-assert them when Shopify injects
      // late. Here the classes are on a node we own and survive hydration, so the
      // gate holds without observing anything.
      var wallet = panel.querySelector(SELECTORS.wallet);
      if (wallet) {
        wallet.setAttribute('data-aero-drawer-wallet-gated', agreed ? 'false' : 'true');
        wallet.setAttribute('aria-disabled', agreed ? 'false' : 'true');
        wallet.classList.toggle('air-pointer-events-none', !agreed);
        wallet.classList.toggle('air-opacity-50', !agreed);
      }
    }

    /**
     * Attach the free-shipping discount code to the CART itself.
     *
     * Why this exists: the regular checkout link routes through
     * /discount/<code>?redirect=/checkout (snippets/aero-cart-checkout-url.liquid),
     * and the code is applied by the act of NAVIGATING there. Accelerated (wallet)
     * buttons never traverse that URL — <shopify-accelerated-checkout-cart> starts
     * its own checkout session from the cart — so without this they would check out
     * with no discount at all.
     *
     * POST /cart/update.js { discount: '<code>' } attaches the code to the cart, so
     * every checkout entry point inherits it. The code itself comes from Liquid
     * (snippets/aero-cart-discount-code.liquid via data-cart-discount) so the URL
     * path and this path can never disagree about which code applies.
     *
     * Fire-and-forget: a failure here must never block add-to-cart or checkout —
     * the regular button still applies the discount via its URL. Runs at most once
     * per page load (attachAttempted) because the code is idempotent and an extra
     * round-trip would only delay the panel swap.
     *
     * Eligibility is Shopify's call, not ours: both codes are segment-scoped in
     * admin (Retail / Reseller VET, keyed on customer_account.price_level), so an
     * ineligible shopper simply has it ignored.
     */
    function attachDiscount() {
      if (attachAttempted || !discountCode) return Promise.resolve();
      attachAttempted = true;

      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: discountCode })
      }).catch(function () {
        /* Discount is a bonus, never a blocker — the regular checkout URL still applies it. */
      });
    }

    function readCartCount(notify) {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (res) {
          return res.json();
        })
        .then(function (cart) {
          var count = typeof cart.item_count === 'number' ? cart.item_count : 0;
          updateCartBubble(count);
          // Only after a real mutation. This also runs on init to true up a
          // cached header badge, and firing there would make every page load
          // look like a cart change to listeners.
          if (notify) notifyCartUpdated(cart);
          return count;
        })
        .catch(function () {
          /* Badge is cosmetic — a failed count must not break the flow. */
        });
    }

    /**
     * Change a line's quantity. quantity: 0 removes it.
     *
     * Mirrors the cart page's error handling (aero-component-cart-page.js):
     * /cart/change.js answers 4xx with a JSON body, so parse it even on a
     * non-OK response and surface `description`/`message`.
     */
    function changeLine(key, quantity, fallbackHref) {
      if (panel) panel.setAttribute('aria-busy', 'true');

      return fetch(changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
          sections: sectionId,
          sections_url: window.location.pathname
        })
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) {
              throw new Error(data.description || data.message || 'Cart update failed');
            }
            return data;
          });
        })
        .then(function (data) {
          var count = typeof data.item_count === 'number' ? data.item_count : 0;
          updateCartBubble(count);
          renderPanel(data.sections && data.sections[sectionId]);
          announce(quantity === 0 ? 'Item removed from cart.' : 'Cart updated.');
          notifyCartUpdated(data);
        })
        .catch(function () {
          // No-JS-equivalent fallback: let the real remove link navigate.
          if (fallbackHref) {
            window.location.href = fallbackHref;
            return;
          }
          // A quantity change has no link to fall back to — reload the panel
          // from the server so the UI cannot be left showing a value the cart
          // never accepted.
          refreshPanel();
          announce('Could not update the cart. Please try again.');
        })
        .then(function () {
          if (panel) panel.removeAttribute('aria-busy');
        });
    }

    /**
     * Re-render the panel from the server without mutating the cart.
     *
     * Used for error recovery and by other cart surfaces that change the cart
     * through their own flow (e.g. the B2B catalog's /cart/update.js) and need
     * the drawer to catch up.
     *
     * Requests `/` rather than the current path. The Section Rendering API only
     * serves sections belonging to the requested page's TEMPLATE, and this
     * section is rendered from layout/theme.liquid — it is in no template at
     * all. Asking `/products/x?sections=…` or `/pages/y?sections=…` therefore
     * returns that page's own sections and the panel silently never updates
     * (verified: the product and page templates both answer without the key).
     * The home template resolves it reliably, and the panel's markup depends
     * only on the `cart` object, so the path it is rendered from does not
     * affect the output.
     */
    function refreshPanel() {
      return fetch(
        window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root + '?sections=' + encodeURIComponent(sectionId)
          : '/?sections=' + encodeURIComponent(sectionId),
        { headers: { Accept: 'application/json' } }
      )
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          renderPanel(data[sectionId]);
          return data;
        })
        .catch(function () {
          /* Leave the stale panel rather than blanking it. */
        });
    }

    // ---- Delegated events (survive the panel's innerHTML swap) --------------

    document.addEventListener('click', function (event) {
      var opener = event.target.closest(SELECTORS.open);
      if (opener) {
        event.preventDefault();
        open(opener);
        return;
      }

      var closer = event.target.closest(SELECTORS.close);
      if (closer && root.contains(closer)) {
        event.preventDefault();
        close();
        return;
      }

      var remove = event.target.closest(SELECTORS.remove);
      if (remove && root.contains(remove)) {
        event.preventDefault();
        changeLine(remove.getAttribute('data-key'), 0, remove.getAttribute('href'));
        return;
      }

      // Block checkout while the terms box is unticked. The link also carries
      // aria-disabled + tabindex=-1, but a mouse click would still follow the
      // href without this.
      var checkout = event.target.closest(SELECTORS.checkout);
      if (checkout && root.contains(checkout)) {
        if (checkout.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          announce('Please agree to the Terms of Sale before checking out.');
        }
      }
    });

    /**
     * Block wallet checkout while the terms box is unticked.
     *
     * CAPTURE phase, unlike the delegated listener above: the accelerated buttons
     * handle clicks inside a closed shadow root and can stop propagation, so a
     * bubble-phase listener on `document` is not guaranteed to see the event at all.
     * (Same reasoning as the capture listener in
     * assets/aero-component-terms-of-sale-agreement.js on the cart page.)
     *
     * The wrapper's `air-pointer-events-none` already swallows mouse clicks; this is
     * the belt-and-braces path for keyboard and assistive tech.
     */
    document.addEventListener(
      'click',
      function (event) {
        var wallet = event.target.closest(SELECTORS.wallet);
        if (!wallet || !root.contains(wallet)) return;
        if (wallet.getAttribute('data-aero-drawer-wallet-gated') !== 'true') return;
        event.preventDefault();
        event.stopPropagation();
        announce('Please agree to the Terms of Sale before checking out.');
      },
      true
    );

    document.addEventListener('change', function (event) {
      var terms = event.target.closest(SELECTORS.terms);
      if (terms && root.contains(terms)) {
        syncCheckoutGate();
        return;
      }

      var qty = event.target.closest(SELECTORS.qty);
      if (qty && root.contains(qty)) {
        var quantity = parseInt(qty.value, 10);
        if (isNaN(quantity) || quantity < 0) return;
        changeLine(qty.getAttribute('data-key'), quantity, null);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isOpen()) close();
    });

    /**
     * Add to cart. Intercepts the PDP form's submit; the native POST to
     * /cart/add is the no-JS path and the failure fallback.
     */
    document.addEventListener('submit', function (event) {
      var form = event.target.closest(SELECTORS.addForm);
      if (!form) return;
      event.preventDefault();

      var body = new FormData(form);
      body.append('sections', sectionId);
      body.append('sections_url', window.location.pathname);

      var submitter = form.querySelector('[type="submit"]');

      fetch(addUrl, { method: 'POST', headers: { Accept: 'application/json' }, body: body })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) {
              throw new Error(data.description || data.message || 'Add to cart failed');
            }
            return data;
          });
        })
        .then(function (data) {
          // Attach the discount BEFORE the drawer opens, so the cart is already
          // correct by the time a shopper can reach a wallet button. Not awaited:
          // it must not delay the panel paint.
          attachDiscount();

          if (data.sections && data.sections[sectionId]) {
            renderPanel(data.sections[sectionId]);
          }

          // Announce the successful add BEFORE opening the drawer. The product
          // quickview listens for this to close its <dialog>: a modal dialog sits
          // in the browser's top layer, so the drawer would otherwise slide open
          // *behind* it. Closing first also means `open()` returns focus to a node
          // that still exists.
          document.dispatchEvent(
            new CustomEvent('aero:cart:added', { detail: { form: form, submitter: submitter } })
          );

          open(submitter);
          announce('Item added to cart.');
          // /cart/add.js does not return the cart total, so read it separately.
          return readCartCount(true);
        })
        .catch(function () {
          // Full POST to /cart/add → lands on /cart. Never strand the shopper.
          form.submit();
        });
    });

    // Initial paint may already contain items (e.g. a returning session).
    syncCheckoutGate();

    // Keep the badge honest on load — the header is often cached separately
    // from the cart, so a stale count is easy to ship.
    readCartCount().then(function (count) {
      // Returning session with items already in the cart: attach the discount so
      // wallet buttons are correct without waiting for an add-to-cart. Skipped on
      // an empty cart — /cart/update.js would 404 and there is nothing to discount.
      // `count` is undefined if the /cart.js read failed (readCartCount swallows
      // errors and resolves with nothing) — the typeof guard keeps that a no-op.
      if (typeof count === 'number' && count > 0) attachDiscount();
    });

    return { open: open, close: close, refresh: refreshPanel, cartUrl: cartUrl };
  }

  function init() {
    var root = document.querySelector(SELECTORS.root);
    if (!root) return;
    // Public handle so other scripts can drive the drawer without re-querying.
    window.AeroCartDrawer = initCartDrawer(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
